import supabase, { getSupabaseAnonKey, getSupabaseBaseUrl } from "@/services/supabaseClient";

const SESSION_EXPIRY_SKEW_MS = 60_000;
const AUTH_TIMEOUT_MS = 8000;

function withAuthTimeout(promise, label = "Auth request") {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      const err = new Error(`${label} timed out`);
      err.code = "auth_timeout";
      setTimeout(() => reject(err), AUTH_TIMEOUT_MS);
    }),
  ]);
}

export function isSessionExpiringSoon(session, skewMs = SESSION_EXPIRY_SKEW_MS) {
  const expiresAtMs = Number(session?.expires_at || 0) * 1000;
  return Number.isFinite(expiresAtMs) && expiresAtMs > 0 && expiresAtMs <= Date.now() + skewMs;
}

async function refreshViaAuthApi(refreshToken) {
  const base = getSupabaseBaseUrl();
  const anon = getSupabaseAnonKey();
  if (!refreshToken || !base || !anon) return null;

  try {
    const res = await withAuthTimeout(
      fetch(`${base}/auth/v1/token?grant_type=refresh_token`, {
        method: "POST",
        headers: {
          apikey: anon,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }),
      "Refresh session",
    );

    const payload = await res.json().catch(() => null);
    if (!res.ok || !payload?.access_token) return null;

    try {
      await supabase.auth.setSession({
        access_token: payload.access_token,
        refresh_token: payload.refresh_token || refreshToken,
      });
    } catch {
      // Best effort only.
    }

    return {
      ...payload,
      refresh_token: payload.refresh_token || refreshToken,
    };
  } catch {
    return null;
  }
}

async function refreshCurrentSession(session = null) {
  try {
    const { data, error } = await withAuthTimeout(
      supabase.auth.refreshSession(),
      "Refresh session",
    );
    if (!error && data?.session) return data.session;
  } catch {
    // Fall through to direct refresh-token call.
  }

  return refreshViaAuthApi(session?.refresh_token);
}

async function validateSession(session) {
  if (!session?.access_token) return null;

  try {
    const { data, error } = await withAuthTimeout(
      supabase.auth.getUser(session.access_token),
      "Validate session",
    );
    if (error || !data?.user) return null;
    return {
      ...session,
      user: data.user,
    };
  } catch {
    return null;
  }
}

export async function resolveValidatedSession({ signOutOnFailure = false } = {}) {
  let session = null;

  try {
    const {
      data: { session: currentSession },
    } = await withAuthTimeout(supabase.auth.getSession(), "Read session");
    session = currentSession || null;
  } catch {
    session = null;
  }

  if (!session) return null;

  if (session.refresh_token && (!session.access_token || isSessionExpiringSoon(session))) {
    session = (await refreshCurrentSession(session)) || session;
  }

  let validated = await validateSession(session);
  if (validated) return validated;

  if (session.refresh_token) {
    const refreshed = await refreshCurrentSession(session);
    validated = await validateSession(refreshed);
    if (validated) return validated;
  }

  if (signOutOnFailure) {
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch {
      // ignore local sign-out errors
    }
  }

  return null;
}
